import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Zap, ChevronRight, Database } from 'lucide-react';

const Header = ({ onGetStarted, variant = 'landing' }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Initialize Google Translate
  useEffect(() => {
    if (!document.getElementById('google-translate-script')) {
      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.body.appendChild(script);
    }

    window.googleTranslateElementInit = function() {
      if (window.google && window.google.translate) {
        new window.google.translate.TranslateElement({
          pageLanguage: 'en',
          includedLanguages: 'en,de,es,fr,it,nl,pl,pt,ru,zh-CN,ja,ar',
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
          autoDisplay: false
        }, 'google_translate_element');
      }
    };

    if (window.google && window.google.translate) {
      window.googleTranslateElementInit();
    }
  }, []);

  const handleGetStarted = () => {
    if (onGetStarted) {
      onGetStarted();
    }
    setMobileMenuOpen(false);
  };

  // Compact header for inner pages
  if (variant === 'compact') {
    return (
      <header className="bg-[#0A0A0A] border-b border-white/10 py-4">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center group-hover:border-[#00F0FF]/50 group-hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</h1>
                <p className="text-xs text-[#888]">Professional ECU Tuning</p>
              </div>
            </Link>
            <div className="flex items-center space-x-4">
              <div id="google_translate_element" className="translate-widget"></div>
              <Link 
                to="/"
                className="flex items-center gap-1 text-[#888] hover:text-[#00F0FF] transition text-sm font-semibold uppercase tracking-wider"
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </header>
    );
  }

  // Full landing page header
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled 
        ? 'bg-[#050505]/95 backdrop-blur-xl border-b border-white/10' 
        : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center group-hover:border-[#00F0FF]/50 group-hover:shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all">
              <Zap className="w-5 h-5 text-[#00F0FF]" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash</h1>
              <p className="text-xs text-[#888] hidden sm:block">Professional Tuning</p>
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <Link to="/services" className="text-[#888] font-semibold uppercase tracking-wider text-sm hover:text-[#00F0FF] transition-colors">
              Services
            </Link>
            <Link to="/how-it-works" className="text-[#888] font-semibold uppercase tracking-wider text-sm hover:text-[#00F0FF] transition-colors">
              How It Works
            </Link>
            <Link to="/pricing" className="text-[#888] font-semibold uppercase tracking-wider text-sm hover:text-[#00F0FF] transition-colors">
              Pricing
            </Link>
            <Link to="/tools/dtc-delete" className="text-[#FF003C] font-semibold uppercase tracking-wider text-sm hover:text-white transition-colors flex items-center gap-1 animate-pulse">
              <Zap className="w-4 h-4" />
              DTC Tool
            </Link>
            <Link to="/file-store" className="text-[#00F0FF] font-semibold uppercase tracking-wider text-sm hover:text-white transition-colors flex items-center gap-1 animate-pulse">
              <Database className="w-4 h-4" />
              File Store
            </Link>
            <Link to="/guides" className="text-[#888] font-semibold uppercase tracking-wider text-sm hover:text-[#00F0FF] transition-colors">
              Guides
            </Link>
            <Link to="/blog" className="text-[#888] font-semibold uppercase tracking-wider text-sm hover:text-[#00F0FF] transition-colors">
              Blog
            </Link>
            <Link to="/portal" className="text-[#888] font-semibold uppercase tracking-wider text-sm hover:text-[#00F0FF] transition-colors">
              Portal
            </Link>
            <div id="google_translate_element" className="translate-widget"></div>
            {onGetStarted && (
              <button 
                onClick={handleGetStarted}
                className="btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-6 py-2.5 hover:bg-white hover:shadow-[0_0_20px_rgba(0,240,255,0.5)] transition-all"
                data-testid="header-get-started-btn"
              >
                <span>Get Started</span>
              </button>
            )}
          </nav>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center bg-[#1A1A1A] border border-white/10 hover:border-[#00F0FF]/50 transition-all"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className="w-5 h-5 text-white" />
            ) : (
              <Menu className="w-5 h-5 text-white" />
            )}
          </button>
        </div>
        
        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4">
            <div className="flex flex-col space-y-1">
              <Link 
                to="/services" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-[#888] font-semibold uppercase tracking-wider text-sm hover:text-[#00F0FF] hover:bg-white/5 transition-all py-3 px-4"
              >
                Services
              </Link>
              <Link 
                to="/how-it-works" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-[#888] font-semibold uppercase tracking-wider text-sm hover:text-[#00F0FF] hover:bg-white/5 transition-all py-3 px-4"
              >
                How It Works
              </Link>
              <Link 
                to="/pricing" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-[#888] font-semibold uppercase tracking-wider text-sm hover:text-[#00F0FF] hover:bg-white/5 transition-all py-3 px-4"
              >
                Pricing
              </Link>
              <Link 
                to="/tools/dtc-delete" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-[#FF003C] font-semibold uppercase tracking-wider text-sm hover:bg-white/5 transition-all py-3 px-4 flex items-center gap-2 animate-pulse"
              >
                <Zap className="w-4 h-4" />
                DTC Delete Tool
              </Link>
              <Link 
                to="/file-store" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-[#00F0FF] font-semibold uppercase tracking-wider text-sm hover:bg-white/5 transition-all py-3 px-4 flex items-center gap-2 animate-pulse"
              >
                <Database className="w-4 h-4" />
                ECU File Store
              </Link>
              <Link 
                to="/blog" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-[#888] font-semibold uppercase tracking-wider text-sm hover:text-[#00F0FF] hover:bg-white/5 transition-all py-3 px-4"
              >
                Blog
              </Link>
              <Link 
                to="/portal" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-[#888] font-semibold uppercase tracking-wider text-sm hover:text-[#00F0FF] hover:bg-white/5 transition-all py-3 px-4"
              >
                Customer Portal
              </Link>
              <Link 
                to="/contact" 
                onClick={() => setMobileMenuOpen(false)}
                className="text-[#888] font-semibold uppercase tracking-wider text-sm hover:text-[#00F0FF] hover:bg-white/5 transition-all py-3 px-4"
              >
                Contact
              </Link>
              <div className="px-4 py-2">
                <div id="google_translate_element_mobile" className="translate-widget"></div>
              </div>
              {onGetStarted && (
                <button 
                  onClick={handleGetStarted}
                  className="mx-4 mt-2 btn-skew bg-[#00F0FF] text-black font-bold uppercase tracking-wider px-6 py-3 text-center hover:bg-white transition-all"
                  data-testid="mobile-get-started-btn"
                >
                  <span>Get Started</span>
                </button>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
